import React, { useState } from 'react';
import { api } from '../api.js';
import { Card, ErrorBanner, Loading, Modal, ProofReference, StateChip, useAsync, useSession, useToast } from '../ui.jsx';
import { dateLabel, money, nameOf } from '../lib/format.js';
import { Link } from '../main.jsx';

/** 設備 browsing per 單位, with 共同承擔者 kept distinct from 代付者. */
export default function EquipmentPage() {
  const session = useSession();
  const [unitId, setUnitId] = useState(session.units[0] ? session.units[0].id : null);
  const [creating, setCreating] = useState(false);
  const equipment = useAsync(() => api.equipment(unitId), [unitId]);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <div className="eyebrow">單位與設備</div>
          <h1>設備與付款責任</h1>
          <p className="small muted">共同承擔者是最終分擔成本的成員；代付者只是先行付款，其後可收回款項。</p>
        </div>
        {session.member.role === 'admin' ? (
          <button type="button" className="primary" onClick={() => setCreating(true)} data-testid="equipment-create">新增設備</button>
        ) : null}
      </div>

      <div className="unit-tabs" role="group" aria-label="選擇單位">
        {session.units.map((unit) => (
          <button
            type="button"
            key={unit.id}
            aria-pressed={unit.id === unitId}
            onClick={() => setUnitId(unit.id)}
            data-testid={`equipment-unit-${unit.name}`}
          >
            {unit.name}
          </button>
        ))}
      </div>

      {equipment.loading ? <Loading /> : null}
      {equipment.error ? <ErrorBanner error={equipment.error} /> : null}
      {equipment.data && equipment.data.equipment.length === 0 ? (
        <Card testId="equipment-empty"><p className="small muted">這個單位暫時沒有設備紀錄。</p></Card>
      ) : null}

      {(equipment.data ? equipment.data.equipment : []).map((entry) => (
        <Card
          key={entry.id}
          title={entry.name}
          subtitle={`${session.units.find((unit) => unit.id === entry.unit_id)?.name || ''}${entry.acquired_on ? ` · 購置 ${dateLabel(entry.acquired_on)}` : ''}`}
          testId={`equipment-${entry.name}`}
          actions={entry.item ? <Link to={`/items/${entry.item.id}`}>查看付款項目</Link> : null}
        >
          {entry.description ? <p className="small">{entry.description}</p> : null}
          <div className="grid two">
            <div>
              <div className="eyebrow">共同承擔者</div>
              <ul className="audit-list" data-testid={`cost-bearers-${entry.name}`}>
                {entry.cost_bearers.length === 0 ? <li className="muted small">未連結付款項目</li> : null}
                {entry.cost_bearers.map((bearer) => (
                  <li key={bearer.member_id}>
                    <strong>{nameOf(session.members, bearer.member_id)}</strong>
                    {bearer.amount_cents === null ? (
                      <span className="small muted"> · 分擔成本（金額只向本人及管理員顯示）</span>
                    ) : (
                      <span className="small"> · {money(bearer.amount_cents)}{bearer.state_label ? ` · ${bearer.state_label}` : ''}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="eyebrow">代付者 / 收款人</div>
              <p data-testid={`payer-${entry.name}`}>
                {entry.payer
                  ? entry.payer.kind === 'member'
                    ? <><strong>{nameOf(session.members, entry.payer.member_id)}</strong> <span className="small muted">先行代付，其後向共同承擔者收回款項</span></>
                    : <><strong>{entry.payer.name}</strong> <span className="small muted">（群組以外的收款人）</span></>
                  : <span className="muted small">未連結付款項目</span>}
              </p>
              {entry.cost_cents !== null ? (
                <div className="metric"><span>設備成本</span><strong data-testid={`equipment-cost-${entry.name}`}>{money(entry.cost_cents)}</strong></div>
              ) : null}
            </div>
          </div>

          {entry.payments.length ? (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th>付款成員</th>
                    <th>證明參考</th>
                    <th className="num">金額</th>
                    <th>狀態</th>
                    <th>日期</th>
                  </tr>
                </thead>
                <tbody>
                  {entry.payments.map((payment) => (
                    <tr key={`${payment.reference}`} data-testid={`equipment-payment-${payment.reference}`}>
                      <td>{nameOf(session.members, payment.member_id)}</td>
                      <td>{payment.kind === 'submission' ? <ProofReference reference={payment.reference} /> : payment.reference}</td>
                      <td className="num">{money(payment.amount_cents)}</td>
                      <td><StateChip state={payment.state} label={payment.state_label} /></td>
                      <td>{dateLabel(payment.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </Card>
      ))}

      {creating ? (
        <CreateEquipmentModal unitId={unitId} onClose={() => setCreating(false)} onDone={() => { setCreating(false); equipment.reload(); }} />
      ) : null}
    </div>
  );
}

function CreateEquipmentModal({ unitId, onClose, onDone }) {
  const notify = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [itemId, setItemId] = useState('');
  const [acquiredOn, setAcquiredOn] = useState('');
  const [error, setError] = useState(null);

  return (
    <Modal title="新增設備" subtitle="連結付款項目後，成本責任會直接沿用既有付款事實" onClose={onClose}>
      <ErrorBanner error={error} />
      <div className="stack">
        <label className="field"><span>設備名稱</span><input value={name} onChange={(event) => setName(event.target.value)} data-testid="equipment-name" /></label>
        <label className="field"><span>描述</span><input value={description} onChange={(event) => setDescription(event.target.value)} data-testid="equipment-description" /></label>
        <label className="field"><span>購置日期</span><input type="date" value={acquiredOn} onChange={(event) => setAcquiredOn(event.target.value)} data-testid="equipment-date" /></label>
        <label className="field"><span>付款項目 ID（選填）</span><input value={itemId} onChange={(event) => setItemId(event.target.value)} data-testid="equipment-item" /></label>
      </div>
      <div className="modal-actions">
        <button type="button" onClick={onClose}>取消</button>
        <button
          type="button"
          className="primary"
          disabled={!name.trim()}
          data-testid="equipment-save"
          onClick={async () => {
            try {
              await api.createEquipment({
                unit_id: unitId,
                name: name.trim(),
                description: description.trim() || null,
                item_id: itemId.trim() || null,
                acquired_on: acquiredOn || null,
              });
              notify('設備已新增');
              onDone();
            } catch (failure) {
              setError(failure);
            }
          }}
        >
          儲存
        </button>
      </div>
    </Modal>
  );
}
